import '../styles/adminResponsive.css';
import React, { useEffect, useState, useCallback, useRef } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  EVENT_TYPE_LABELS, DAY_LABELS, TIME_RANGE_LABELS, TECH_LABELS,
  BUDGET_MODEL_LABELS, FIELD_LABELS,
} from '../constants/canonicalEnums';

const API = process.env.REACT_APP_BACKEND_URL;

const STATUS_FILTERS = [
  { value: 'admin_curating', label: 'Aday İnceleme', color: 'bg-amber-100 text-amber-900' },
  { value: 'admin_approved', label: 'Onaylı Adaylar', color: 'bg-emerald-100 text-emerald-900' },
  { value: 'admin_rejected', label: 'Reddedilen Adaylar', color: 'bg-zinc-100 text-zinc-600' },
  { value: 'pending_final', label: 'Final Onay Bekleyen', color: 'bg-violet-100 text-violet-900' },
  { value: 'approved', label: 'Eşleşti', color: 'bg-emerald-600 text-white' },
];

const STATUS_LABELS = Object.fromEntries(STATUS_FILTERS.map(s => [s.value, s.label]));

// --- Diff sub-component ---
const FilterDiff = ({ reqId }) => {
  const [diff, setDiff] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let mounted = true;
    setLoading(true);
    axios.get(`${API}/api/admin/event-requests/${reqId}/diff`)
      .then(({ data }) => { if (mounted) setDiff(data); })
      .catch(err => { if (mounted) toast.error('Diff yüklenemedi'); })
      .finally(() => { if (mounted) setLoading(false); });
    return () => { mounted = false; };
  }, [reqId]);

  if (loading) return <div className="text-sm text-zinc-500 italic">Diff yükleniyor…</div>;
  if (!diff) return null;

  const formatVal = (v) => {
    if (v == null || v === '') return <span className="text-zinc-400 italic">boş</span>;
    if (Array.isArray(v)) {
      if (v.length === 0) return <span className="text-zinc-400 italic">boş</span>;
      return v.join(', ');
    }
    if (typeof v === 'boolean') return v ? 'Evet' : 'Hayır';
    return String(v);
  };

  const labelize = (field, val) => {
    if (val == null) return val;
    const tables = {
      event_type: EVENT_TYPE_LABELS,
      preferred_days: DAY_LABELS,
      preferred_time_ranges: TIME_RANGE_LABELS,
      technical_needs: TECH_LABELS,
      venue_budget_model: BUDGET_MODEL_LABELS,
    };
    if (tables[field]) {
      if (Array.isArray(val)) return val.map(v => tables[field][v] || v);
      return tables[field][val] || val;
    }
    return val;
  };

  return (
    <div data-testid={`diff-${reqId}`} className="bg-zinc-50 border border-zinc-200 rounded-xl p-4 space-y-3">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <h4 className="font-bold text-sm">AI çıktısı vs. Organizatör onayı</h4>
        {diff.analysis_confirmed_at && (
          <span className="text-xs text-zinc-500">Onay: {new Date(diff.analysis_confirmed_at).toLocaleString('tr-TR')}</span>
        )}
      </div>
      {diff.free_text && (
        <div className="text-xs">
          <span className="font-semibold text-zinc-700">Serbest metin:</span>
          <p className="mt-1 italic text-zinc-600">&ldquo;{diff.free_text}&rdquo;</p>
        </div>
      )}
      {diff.follow_up_questions?.length > 0 && (
        <div className="text-xs">
          <span className="font-semibold text-zinc-700">AI&apos;nın sorduğu:</span>
          <ul className="mt-1 ml-3 space-y-0.5 text-zinc-600">
            {diff.follow_up_questions.map((q, i) => <li key={i}>• {q}</li>)}
          </ul>
        </div>
      )}
      <div className="overflow-x-auto">
        <table className="w-full text-xs">
          <thead>
            <tr className="text-zinc-500 border-b border-zinc-200">
              <th className="text-left py-1.5 pr-3 font-medium">Alan</th>
              <th className="text-left py-1.5 pr-3 font-medium">AI Çıkarımı</th>
              <th className="text-left py-1.5 pr-3 font-medium">Organizatör Onayı</th>
            </tr>
          </thead>
          <tbody>
            {diff.diff.map(row => (
              <tr key={row.field} className={`border-b border-zinc-100 ${row.changed ? 'bg-amber-50/60' : ''}`}>
                <td className="py-1.5 pr-3 font-medium text-zinc-700">
                  {FIELD_LABELS[row.field] || row.field}
                  {row.changed && <span className="ml-1.5 text-amber-700">⚠️</span>}
                </td>
                <td className="py-1.5 pr-3 text-zinc-600">{formatVal(labelize(row.field, row.ai_extracted))}</td>
                <td className="py-1.5 pr-3 font-semibold text-zinc-800">{formatVal(labelize(row.field, row.organizer_confirmed))}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

// --- Audit event labels ---
const EVENT_LABELS = {
  created: { label: 'Aday oluşturuldu', icon: '🌱', color: 'text-zinc-600' },
  regenerated: { label: 'Aday yeniden hesaplandı', icon: '♻️', color: 'text-zinc-600' },
  score_updated: { label: 'Admin skor verdi', icon: '🎯', color: 'text-[#1E3A8A]' },
  notes_updated: { label: 'Admin not güncelledi', icon: '📝', color: 'text-sky-600' },
  curated_approved: { label: 'Admin onayladı (taraflara açtı)', icon: '✓', color: 'text-emerald-600' },
  curated_rejected: { label: 'Admin reddetti', icon: '✕', color: 'text-red-500' },
  organizer_liked: { label: 'Organizatör beğendi', icon: '👍', color: 'text-violet-600' },
  venue_liked: { label: 'Mekân beğendi', icon: '👍', color: 'text-violet-600' },
  mutual_like: { label: 'Karşılıklı beğeni — admin onay bekliyor', icon: '🤝', color: 'text-amber-600' },
  final_approved: { label: 'Final onaylandı (eşleşti)', icon: '🎉', color: 'text-emerald-700' },
  final_rejected: { label: 'Final reddedildi', icon: '⛔', color: 'text-red-600' },
};

const HistoryTimeline = ({ matchId }) => {
  const [history, setHistory] = useState(null);
  useEffect(() => {
    let mounted = true;
    axios.get(`${API}/api/admin/match-candidates/${matchId}/history`)
      .then(({ data }) => { if (mounted) setHistory(data.history || []); })
      .catch(() => { if (mounted) setHistory([]); });
    return () => { mounted = false; };
  }, [matchId]);
  if (history === null) return <div className="text-xs text-zinc-500 italic">Geçmiş yükleniyor…</div>;
  if (history.length === 0) return <div className="text-xs text-zinc-500 italic">Henüz olay yok.</div>;
  return (
    <div data-testid={`history-${matchId}`} className="space-y-2 max-h-72 overflow-y-auto pr-1">
      {[...history].reverse().map((h, i) => {
        const meta = EVENT_LABELS[h.event] || { label: h.event, icon: '·', color: 'text-zinc-500' };
        return (
          <div key={i} className="flex gap-2 text-xs items-start border-l-2 border-zinc-100 pl-3 py-1">
            <span className="text-base leading-none mt-0.5">{meta.icon}</span>
            <div className="flex-1 min-w-0">
              <div className={`font-semibold ${meta.color}`}>{meta.label}</div>
              <div className="text-zinc-500 mt-0.5">
                {new Date(h.at).toLocaleString('tr-TR')}
                {h.by?.email && ` · ${h.by.email}`}
                {!h.by?.email && h.by?.role && ` · ${h.by.role}`}
              </div>
              {h.payload && Object.keys(h.payload).length > 0 && (
                <details className="mt-1">
                  <summary className="text-zinc-400 cursor-pointer hover:text-zinc-600">detay</summary>
                  <pre className="text-[10px] bg-zinc-50 border border-zinc-200 rounded px-2 py-1 mt-1 overflow-x-auto whitespace-pre-wrap">
{JSON.stringify(h.payload, null, 2)}
                  </pre>
                </details>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
};

const CandidateEditor = ({ match, onSave, saving }) => {
  const [form, setForm] = useState({
    admin_score: match.admin_score ?? '',
    admin_note_internal: match.admin_note_internal || '',
    admin_note_risk: match.admin_note_risk || '',
    admin_note_for_organizer: match.admin_note_for_organizer || '',
  });
  const [dirty, setDirty] = useState(false);
  useEffect(() => {
    setForm({
      admin_score: match.admin_score ?? '',
      admin_note_internal: match.admin_note_internal || '',
      admin_note_risk: match.admin_note_risk || '',
      admin_note_for_organizer: match.admin_note_for_organizer || '',
    });
    setDirty(false);
  }, [match.id, match.admin_score, match.admin_note_internal, match.admin_note_risk, match.admin_note_for_organizer]);

  const set = (k, v) => { setForm(prev => ({ ...prev, [k]: v })); setDirty(true); };

  return (
    <div className="bg-zinc-50 border-2 border-zinc-100 rounded-xl p-4 mb-3" data-testid={`editor-${match.id}`}>
      <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
        <p className="text-sm font-bold">Admin Değerlendirme</p>
        {match.admin_score != null && (
          <span className="text-xs px-2 py-0.5 rounded-full bg-[#1E3A8A] text-white font-bold">
            Mevcut skor: {match.admin_score}/10
          </span>
        )}
      </div>

      <div className="grid sm:grid-cols-12 gap-3">
        {/* Score 1-10 */}
        <div className="sm:col-span-3">
          <label className="block text-xs font-semibold text-zinc-600 mb-1">Manuel Skor (1-10)</label>
          <div className="flex items-center gap-2">
            <input type="number" min="1" max="10" step="1"
              data-testid={`editor-score-${match.id}`}
              value={form.admin_score}
              onChange={e => set('admin_score', e.target.value === '' ? '' : parseInt(e.target.value))}
              className="w-20 px-3 py-2 border-2 border-zinc-200 rounded-lg focus:outline-none focus:border-[#1E3A8A] text-center font-bold text-lg" />
            <span className="text-sm text-zinc-500">/ 10</span>
          </div>
        </div>

        {/* Internal note */}
        <div className="sm:col-span-9">
          <label className="block text-xs font-semibold text-zinc-600 mb-1">
            🔒 İç Admin Notu <span className="text-zinc-400 font-normal">(sadece admin görür)</span>
          </label>
          <textarea rows={2}
            data-testid={`editor-internal-${match.id}`}
            value={form.admin_note_internal}
            onChange={e => set('admin_note_internal', e.target.value)}
            placeholder="Telefon görüşmesi notu, takip durumu, vb."
            className="w-full px-3 py-2 border-2 border-zinc-200 rounded-lg focus:outline-none focus:border-[#1E3A8A] text-sm" />
        </div>

        {/* Risk note */}
        <div className="sm:col-span-6">
          <label className="block text-xs font-semibold text-zinc-600 mb-1">
            ⚠️ Risk Notu <span className="text-zinc-400 font-normal">(sadece admin görür)</span>
          </label>
          <textarea rows={2}
            data-testid={`editor-risk-${match.id}`}
            value={form.admin_note_risk}
            onChange={e => set('admin_note_risk', e.target.value)}
            placeholder="Ses izolasyonu, ulaşım, ödeme hassasiyeti gibi riskler"
            className="w-full px-3 py-2 border-2 border-amber-200 rounded-lg focus:outline-none focus:border-amber-500 text-sm bg-amber-50/50" />
        </div>

        {/* Organizer-facing note */}
        <div className="sm:col-span-6">
          <label className="block text-xs font-semibold text-zinc-600 mb-1">
            💎 Organizatöre Gösterilecek Açıklama
          </label>
          <textarea rows={2}
            data-testid={`editor-org-${match.id}`}
            value={form.admin_note_for_organizer}
            onChange={e => set('admin_note_for_organizer', e.target.value)}
            placeholder="Organizatörün kartta göreceği kısa açıklama"
            className="w-full px-3 py-2 border-2 border-emerald-200 rounded-lg focus:outline-none focus:border-emerald-500 text-sm bg-emerald-50/30" />
        </div>
      </div>

      <div className="flex items-center justify-end gap-2 mt-3">
        {dirty && <span className="text-xs text-amber-700 font-semibold">● Değişiklikler kaydedilmedi</span>}
        <button
          onClick={() => onSave({ ...form, expected_updated_at: match.updated_at || null })}
          disabled={saving || !dirty}
          data-testid={`editor-save-${match.id}`}
          className="px-5 py-2 bg-white border-2 border-[#1E3A8A] text-[#1E3A8A] hover:bg-[#1E3A8A] hover:text-white rounded-lg font-bold text-sm transition-colors disabled:opacity-50"
        >
          {saving ? 'Kaydediliyor…' : 'Skoru & Notları Kaydet'}
        </button>
      </div>
    </div>
  );
};

// --- Main ---
const AdminMatches = () => {
  const [matches, setMatches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('admin_curating');
  const [notesById, setNotesById] = useState({});
  const [openDiffs, setOpenDiffs] = useState({});
  const [openHistory, setOpenHistory] = useState({});
  const [savingById, setSavingById] = useState({});
  const [counts, setCounts] = useState({});

  const requestGeneration = useRef(0);
  const fetchMatches = useCallback(async () => {
    const generation = ++requestGeneration.current;
    setLoading(true);
    try {
      // Curate aşaması için admin endpoint, diğerleri için genel /matches
      const url = ['admin_curating', 'admin_approved', 'admin_rejected'].includes(filter)
        ? `${API}/api/admin/match-candidates?status_filter=${filter}`
        : `${API}/api/matches`;
      const { data } = await axios.get(url);
      const filtered = ['admin_curating', 'admin_approved', 'admin_rejected'].includes(filter)
        ? data
        : data.filter(m => m.status === filter);
      if (generation === requestGeneration.current) setMatches(filtered);
    } catch (err) {
      if (generation === requestGeneration.current) toast.error(err.response?.data?.detail || 'Yüklenemedi');
    } finally {
      if (generation === requestGeneration.current) setLoading(false);
    }
  }, [filter]);

  const fetchCounts = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/api/admin/match-counts`);
      setCounts(data || {});
    } catch (err) {
      // count failure non-fatal
    }
  }, []);

  useEffect(() => { fetchMatches(); return () => { requestGeneration.current += 1; }; }, [fetchMatches]);
  useEffect(() => { fetchCounts(); }, [fetchCounts]);

  const generateMatches = async () => {
    try {
      const { data } = await axios.post(`${API}/api/matches/generate`, {});
      toast.success(data.message);
      fetchMatches(); fetchCounts();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Bir hata oluştu');
    }
  };

  // Admin curate: admin_curating → admin_approved/rejected
  const handleCurate = async (matchId, approve) => {
    try {
      await axios.put(`${API}/api/admin/match-candidates/${matchId}/curate`,
        { approve, notes: notesById[matchId] || '' });
      toast.success(approve ? 'Aday onaylandı (taraflara açıldı)' : 'Aday reddedildi');
      fetchMatches(); fetchCounts();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Bir hata oluştu');
    }
  };

  // Final approval: pending_final → approved/rejected
  const handleFinalApprove = async (matchId, approve) => {
    try {
      await axios.put(`${API}/api/matches/${matchId}/approve`,
        { approve, notes: notesById[matchId] || '' });
      toast.success(approve ? 'Eşleşme onaylandı' : 'Eşleşme reddedildi');
      fetchMatches(); fetchCounts();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Bir hata oluştu');
    }
  };

  const toggleDiff = (reqId) => setOpenDiffs(prev => ({ ...prev, [reqId]: !prev[reqId] }));
  const toggleHistory = (matchId) => setOpenHistory(prev => ({ ...prev, [matchId]: !prev[matchId] }));

  const handleSavePartial = async (matchId, payload) => {
    setSavingById(prev => ({ ...prev, [matchId]: true }));
    try {
      // Skor boş gelirse null gönderme (backend zaten None ise atlar)
      const body = {
        expected_updated_at: payload.expected_updated_at,
        admin_score: payload.admin_score === '' ? null : payload.admin_score,
        admin_note_internal: payload.admin_note_internal,
        admin_note_risk: payload.admin_note_risk,
        admin_note_for_organizer: payload.admin_note_for_organizer,
      };
      await axios.put(`${API}/api/admin/match-candidates/${matchId}`, body);
      toast.success('Kaydedildi');
      fetchMatches(); fetchCounts();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Kaydedilemedi');
    } finally {
      setSavingById(prev => ({ ...prev, [matchId]: false }));
    }
  };

  return (
    <div className="p-8 max-w-7xl mx-auto admin-responsive-page">
      <div className="mb-8 flex items-start justify-between flex-wrap gap-4">
        <div>
          <p className="text-sm text-[#1E3A8A] font-bold mb-1">Admin Eşleşme Paneli</p>
          <h1 className="text-3xl font-bold tracking-tight" data-testid="admin-matches-title">Aday İnceleme & Onay</h1>
          <p className="text-sm text-zinc-600 mt-2">
            AI mekân seçmez; deterministik filtre motoru aday üretir, sen curate edip taraflara açarsın. Karşılıklı beğeni sonrası final onay senin.
          </p>
        </div>
        <button onClick={generateMatches} data-testid="admin-generate-matches-button"
          className="px-5 py-2.5 bg-white border-2 border-[#1E3A8A] text-[#1E3A8A] hover:bg-[#1E3A8A] hover:text-white rounded-xl font-bold transition-colors">
          Adayları Yeniden Hesapla
        </button>
      </div>

      {/* Tabs */}
      <div className="flex flex-wrap gap-2 mb-6" data-testid="admin-matches-tabs">
        {STATUS_FILTERS.map(s => (
          <button key={s.value} onClick={() => setFilter(s.value)}
            data-testid={`admin-matches-filter-${s.value}`}
            className={`px-4 py-2 text-sm rounded-full font-bold transition-colors ${
              filter === s.value
                ? 'bg-[#1E3A8A] text-white'
                : 'bg-white border-2 border-zinc-200 hover:border-[#1E3A8A] text-zinc-700'
            }`}>
            {s.label}
            <span className="ml-2 px-2 py-0.5 rounded-full bg-black/10 text-xs tabular-nums">
              {counts[s.value] ?? '·'}
            </span>
          </button>
        ))}
      </div>

      {loading ? (
        <div className="p-8 text-center text-zinc-500">Yükleniyor…</div>
      ) : matches.length === 0 ? (
        <div className="border-2 border-dashed border-zinc-200 p-12 text-center rounded-2xl">
          <p className="text-sm text-zinc-500">Bu kategoride kayıt yok.</p>
        </div>
      ) : (
        <div className="space-y-4" data-testid="admin-matches-list">
          {matches.map(m => (
            <div key={m.id} className="bg-white border-2 border-zinc-200 rounded-2xl p-6 admin-responsive-card" data-testid={`admin-match-${m.id}`}>
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-4">
                <div>
                  <p className="text-xs font-bold text-zinc-500 uppercase">Mekân</p>
                  <p className="font-bold mt-0.5">{m.venue_name}</p>
                </div>
                <div>
                  <p className="text-xs font-bold text-zinc-500 uppercase">Etkinlik</p>
                  <p className="font-bold mt-0.5">{m.event_title || m.organizer_name}</p>
                  <p className="text-xs text-zinc-500">{m.organizer_name}</p>
                </div>
                <div>
                  <p className="text-xs font-bold text-zinc-500 uppercase">Uyum Skoru</p>
                  <p className="font-mono text-3xl font-black text-[#1E3A8A] mt-0.5">{m.compatibility_score}</p>
                </div>
                <div>
                  <p className="text-xs font-bold text-zinc-500 uppercase">Durum</p>
                  <span className="mt-1 inline-block px-2.5 py-1 rounded-full bg-zinc-100 text-zinc-800 text-xs font-bold">
                    {STATUS_LABELS[m.status] || m.status}
                  </span>
                </div>
              </div>

              {(m.reasons_positive?.length > 0 || m.reasons_negative?.length > 0) && (
                <div className="grid md:grid-cols-2 gap-3 mb-4">
                  <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-3">
                    <p className="text-xs font-bold text-emerald-900 mb-1.5">+ Uyumlu</p>
                    <ul className="space-y-0.5">
                      {(m.reasons_positive || []).map((r, i) => (
                        <li key={i} className="text-xs text-emerald-900">{r}</li>
                      ))}
                      {!m.reasons_positive?.length && <li className="text-xs text-zinc-400 italic">—</li>}
                    </ul>
                  </div>
                  <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
                    <p className="text-xs font-bold text-amber-900 mb-1.5">− Eksik / Uyumsuz</p>
                    <ul className="space-y-0.5">
                      {(m.reasons_negative || []).map((r, i) => (
                        <li key={i} className="text-xs text-amber-900">{r}</li>
                      ))}
                      {!m.reasons_negative?.length && <li className="text-xs text-zinc-400 italic">Tümü uyumlu</li>}
                    </ul>
                  </div>
                </div>
              )}

              {/* Filter diff + history toggles */}
              <div className="flex flex-wrap gap-3 mb-3">
                {m.event_request_id && (
                  <button onClick={() => toggleDiff(m.event_request_id)}
                    data-testid={`diff-toggle-${m.id}`}
                    className="text-xs font-bold text-[#1E3A8A] hover:underline">
                    {openDiffs[m.event_request_id] ? '▼' : '▶'} AI vs Organizatör Filtre Diff&apos;i
                  </button>
                )}
                <button onClick={() => toggleHistory(m.id)}
                  data-testid={`history-toggle-${m.id}`}
                  className="text-xs font-bold text-zinc-600 hover:underline">
                  {openHistory[m.id] ? '▼' : '▶'} Geçmiş (audit)
                </button>
              </div>
              {openDiffs[m.event_request_id] && m.event_request_id && (
                <div className="mb-3">
                  <FilterDiff reqId={m.event_request_id} />
                </div>
              )}
              {openHistory[m.id] && (
                <div className="mb-3 bg-white border border-zinc-200 rounded-xl p-4">
                  <HistoryTimeline matchId={m.id} />
                </div>
              )}

              {/* Score & Notes editor — yalnızca admin'in aktif aşamasında */}
              {(m.status === 'admin_curating' || m.status === 'admin_approved' || m.status === 'pending_final') && (
                <CandidateEditor
                  match={m}
                  saving={!!savingById[m.id]}
                  onSave={(payload) => handleSavePartial(m.id, payload)}
                />
              )}

              {/* Actions */}
              {m.status === 'admin_curating' && (
                <div className="pt-3 border-t border-zinc-100">
                  <input type="text" placeholder="Curate notu (opsiyonel — kapanış notu)"
                    value={notesById[m.id] || ''}
                    onChange={e => setNotesById({ ...notesById, [m.id]: e.target.value })}
                    data-testid={`admin-match-notes-${m.id}`}
                    className="w-full px-3 py-2 border border-zinc-200 rounded-lg text-sm focus:outline-none focus:border-[#1E3A8A] mb-3" />
                  <div className="flex gap-2">
                    <button onClick={() => handleCurate(m.id, true)} data-testid={`admin-curate-approve-${m.id}`}
                      className="px-5 py-2.5 bg-[#1E3A8A] hover:bg-[#1E3A8A] text-white rounded-lg font-bold text-sm transition-colors">
                      ✓ Adayı Aç (Taraflara Göster)
                    </button>
                    <button onClick={() => handleCurate(m.id, false)} data-testid={`admin-curate-reject-${m.id}`}
                      className="px-5 py-2.5 bg-white border-2 border-zinc-300 hover:border-red-400 hover:text-red-500 text-zinc-700 rounded-lg font-bold text-sm transition-colors">
                      ✕ Reddet
                    </button>
                  </div>
                </div>
              )}

              {m.status === 'pending_final' && (
                <div className="pt-3 border-t border-zinc-100">
                  <input type="text" placeholder="Admin notu (isteğe bağlı)"
                    value={notesById[m.id] || ''}
                    onChange={e => setNotesById({ ...notesById, [m.id]: e.target.value })}
                    data-testid={`admin-match-notes-${m.id}`}
                    className="w-full px-3 py-2 border border-zinc-200 rounded-lg text-sm focus:outline-none focus:border-[#1E3A8A] mb-3" />
                  <div className="flex gap-2">
                    <button onClick={() => handleFinalApprove(m.id, true)} data-testid={`admin-final-approve-${m.id}`}
                      className="px-5 py-2.5 bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg font-bold text-sm transition-colors">
                      ✓ Final Eşleşmeyi Onayla
                    </button>
                    <button onClick={() => handleFinalApprove(m.id, false)} data-testid={`admin-final-reject-${m.id}`}
                      className="px-5 py-2.5 bg-white border-2 border-zinc-300 hover:border-red-400 hover:text-red-500 text-zinc-700 rounded-lg font-bold text-sm transition-colors">
                      ✕ Reddet
                    </button>
                  </div>
                </div>
              )}

              {m.admin_notes && (
                <div className="mt-3 p-3 bg-zinc-50 border border-zinc-200 rounded-lg">
                  <p className="text-xs font-bold text-zinc-500 uppercase mb-1">Admin Notu</p>
                  <p className="text-sm">{m.admin_notes}</p>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default AdminMatches;
